// Binary writer/reader + the event schema. Everything on the wire is little-endian.

export class Writer {
  buf: ArrayBuffer;
  dv: DataView;
  u8: Uint8Array;
  o = 0;
  constructor(size = 2048) {
    this.buf = new ArrayBuffer(size);
    this.dv = new DataView(this.buf);
    this.u8 = new Uint8Array(this.buf);
  }
  reset() { this.o = 0; return this; }
  private need(n: number) {
    if (this.o + n <= this.buf.byteLength) return;
    let size = this.buf.byteLength * 2;
    while (size < this.o + n) size *= 2;
    const nb = new ArrayBuffer(size);
    new Uint8Array(nb).set(this.u8);
    this.buf = nb; this.dv = new DataView(nb); this.u8 = new Uint8Array(nb);
  }
  u8w(v: number) { this.need(1); this.dv.setUint8(this.o, v); this.o += 1; }
  i8w(v: number) { this.need(1); this.dv.setInt8(this.o, v); this.o += 1; }
  u16w(v: number) { this.need(2); this.dv.setUint16(this.o, v, true); this.o += 2; }
  i16w(v: number) { this.need(2); this.dv.setInt16(this.o, v, true); this.o += 2; }
  u32w(v: number) { this.need(4); this.dv.setUint32(this.o, v >>> 0, true); this.o += 4; }
  f32w(v: number) { this.need(4); this.dv.setFloat32(this.o, v, true); this.o += 4; }
  f64w(v: number) { this.need(8); this.dv.setFloat64(this.o, v, true); this.o += 8; }
  /** position component at 1 cm, ±327 m */
  cmw(v: number) { this.i16w(Math.max(-32767, Math.min(32767, Math.round(v * 100)))); }
  /** angle as u16 */
  angw(v: number) { this.u16w(Math.round(((v % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) / (Math.PI * 2) * 65535)); }
  /** unit-range value as i8 */
  unitw(v: number) { this.i8w(Math.max(-127, Math.min(127, Math.round(v * 127)))); }
  strw(s: string) {
    const b = new TextEncoder().encode(s);
    this.u16w(b.length); this.need(b.length); this.u8.set(b, this.o); this.o += b.length;
  }
  bytes() { return this.u8.slice(0, this.o); }
}

export class Reader {
  dv: DataView;
  o = 0;
  constructor(data: ArrayBuffer | Uint8Array) {
    if (data instanceof Uint8Array) this.dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
    else this.dv = new DataView(data);
  }
  get left() { return this.dv.byteLength - this.o; }
  u8() { return this.dv.getUint8(this.o++); }
  i8() { return this.dv.getInt8(this.o++); }
  u16() { const v = this.dv.getUint16(this.o, true); this.o += 2; return v; }
  i16() { const v = this.dv.getInt16(this.o, true); this.o += 2; return v; }
  u32() { const v = this.dv.getUint32(this.o, true); this.o += 4; return v; }
  f32() { const v = this.dv.getFloat32(this.o, true); this.o += 4; return v; }
  f64() { const v = this.dv.getFloat64(this.o, true); this.o += 8; return v; }
  cm() { return this.i16() / 100; }
  ang() { const a = (this.u16() / 65535) * Math.PI * 2; return a > Math.PI ? a - Math.PI * 2 : a; }
  unit() { return this.i8() / 127; }
  str() {
    const n = this.u16();
    const s = new TextDecoder().decode(new Uint8Array(this.dv.buffer, this.dv.byteOffset + this.o, n));
    this.o += n; return s;
  }
}

// ---------------------------------------------------------------- events
export const enum Ev {
  Shot = 1, EnemyHit, EnemyDie, Prime, Detonate, PlayerHurt, BulletSpawn, BulletEnd, Explosion,
  Down, Revive, Respawn, Wave, Melee, Special, SpawnWarn, Roster, Notice, Chain, Unprime, Stagger, Lives, Hazard,
  // the run (sim/run.ts): island swap, phase change, boss beats, the bell, livestock, the end
  Island, Run, Boss, Bell, Critter, RunEnd,
  // power-ups (sim/powerups.ts)
  PowerUp,
  // weapons lane (sim/arsenal.ts)
  OrdSpawn, OrdEnd, Crate, Turret, TurretShot, Proc, Throw, OrdRest,
  Mark, Nemesis, FriendlyHit,
  // the saloon stage (sim/saloon.ts): explicit ids so other lanes can append above without renumbering
  Stage = 60, ReadyState = 61,
  // the Service Record (sim/progression.ts): the contract the posse signed for the next ride
  Contract = 62,
  // destruction (sim/destruct.ts): a building's damage stage, a stone toppled by a blast
  Building = 63, Topple = 64, StoneRest = 65,
  // whiskey (sim/whiskey.ts): a cowboy uncorked a bottle
  Drink = 66,
  // the saloon: who sits where (sim/saloon.ts SaloonStage.sit)
  Seat = 67, // 66 is Ev.Drink (whiskey lane)
  // weight lane: a cowboy was floored (KnockKind 1) or flung into a ragdoll (2) by a hit; kx kz is the shove
  Knock = 68,
  // class verbs (CLASSES.md): the Phantom's blink strike (pid, robot, from xz, to xyz); the Valkyrie's ground pound (pid, xyz, radius)
  Blink = 69, Pound = 70,
  // armour (ARMOUR.md): a round glanced off armour and bounced: pid, from xyz, dir, dist, what it hit next (0 air 1 world 2 body 5 a cowboy), robot id, weapon
  Ricochet = 71,
  // wildcard cards (sim/cards.ts): pid, what happened (1 a draft is on the table: its kind; 2 picked: the card; 3 passed), value
  Card = 72,
  // the hexed strongbox (CARDS.md): x y z, state (1 placed, 0 opened), who opened it (255 none)
  Strongbox = 73,
  // elemental barrels (ROSTER.md DISMANTLE): id, element (Elem + 1), x y z, state (1 placed, 0 gone), cluster level
  Barrel = 74,
  // visible damage (playtest 3): a part came off a living robot: robot id, part (defs.ts Part), where
  PartPop = 75,
  // a cowboy flew through a house wall (sim/destruct.ts BREACH): island, building, hole x y z, face (0 +x 1 -x 2 +z 3 -z, | 4 = the way out), pid (255 = replay)
  Breach = 76,
}

type K = 'u8' | 'u16' | 'u32' | 'f32' | 'cm' | 'unit' | 's';
/** Field layout per event type. Every event is { t, f: number[], s?: string }. */
export const EV_SCHEMA: Record<number, K[]> = {
  // pid, ox oy oz, dx dy dz, dist, hitKind(0 none 1 world 2 body 3 head 4 armor), eid, weapon id
  [Ev.Shot]: ['u8', 'f32', 'f32', 'f32', 'unit', 'unit', 'unit', 'f32', 'u8', 'u16', 'u8', 'u8'],
  // eid, dmg, flags(1 head 2 shield 4 armor 8 detonation 16 burn), pid, x y z
  [Ev.EnemyHit]: ['u16', 'u16', 'u8', 'u8', 'f32', 'f32', 'f32'],
  // eid, type, x y z, killer pid
  [Ev.EnemyDie]: ['u16', 'u8', 'f32', 'f32', 'f32', 'u8'],
  [Ev.Prime]: ['u16', 'u8'],
  // pid, bottles left
  [Ev.Drink]: ['u8', 'u8', 'u8'],
  // eid, pid, flavor class, x y z, radius, chain count, cross-player
  [Ev.Detonate]: ['u16', 'u8', 'u8', 'f32', 'f32', 'f32', 'f32', 'u8', 'u8'],
  // pid, dmg, from x, from z, flags(1 shield broke 2 heat 4 melee)
  [Ev.PlayerHurt]: ['u8', 'u16', 'f32', 'f32', 'u8'],
  // bid, kind, spawn tick, x y z, vx vy vz, owner eid
  [Ev.BulletSpawn]: ['u16', 'u8', 'u32', 'f32', 'f32', 'f32', 'f32', 'f32', 'f32', 'u16'],
  // bid, x y z, hit pid (255 none)
  [Ev.BulletEnd]: ['u16', 'f32', 'f32', 'f32', 'u8'],
  // x y z, radius, kind
  [Ev.Explosion]: ['f32', 'f32', 'f32', 'f32', 'u8'],
  [Ev.Down]: ['u8'],
  [Ev.Revive]: ['u8', 'u8'],
  [Ev.Respawn]: ['u8'],
  // wave, state, tier
  [Ev.Wave]: ['u8', 'u8', 'u8'],
  // pid, hits, primed count, blade flourish step (0 the draw strike, 1..3)
  [Ev.Melee]: ['u8', 'u8', 'u8', 'u8'],
  [Ev.Special]: ['u8'],
  // x y z, type, ticks
  [Ev.SpawnWarn]: ['f32', 'f32', 'f32', 'u8', 'u8'],
  [Ev.Roster]: ['s'],
  [Ev.Notice]: ['s'],
  // pid, count
  [Ev.Chain]: ['u8', 'u8'],
  [Ev.Unprime]: ['u16'],
  [Ev.Stagger]: ['u16'],
  [Ev.Lives]: ['u8'],
  // hazard id, element (renderer El: 1 acid-green gas), x y z, radius, ticks
  [Ev.Hazard]: ['u16', 'u8', 'f32', 'f32', 'f32', 'f32', 'u16'],
  // island index (whole run), kind (0 combat 1 boss), village, island in village, name
  [Ev.Island]: ['u16', 'u8', 'u8', 'u8', 's'],
  // RunPhase, timer ticks, wave, waves
  [Ev.Run]: ['u8', 'u16', 'u8', 'u8'],
  // boss eid, BossEv, a, b (attack id + head, head index...)
  [Ev.Boss]: ['u16', 'u8', 'u8', 'u8'],
  // pid who rang it
  [Ev.Bell]: ['u8'],
  // critter id, 0 hurt / 1 died, by pid (255 none)
  [Ev.Critter]: ['u8', 'u8', 'u8'],
  // result (0 extracted 1 wiped), village, island
  [Ev.RunEnd]: ['u8', 'u8', 'u8'],
  // pid, PU type, element + 1, PUEv (picked / used / expired / denied), pickup id
  [Ev.PowerUp]: ['u8', 'u8', 'u8', 'u8', 'u8'],
  // ordnance id, OKind, sub (grenade type / weapon id), spawn tick, x y z, vx vy vz, owner pid, homing target eid
  [Ev.OrdSpawn]: ['u16', 'u8', 'u8', 'u32', 'f32', 'f32', 'f32', 'f32', 'f32', 'f32', 'u8', 'u16'],
  // ordnance id, x y z, how (1 world 2 robot 3 expired / fuse 4 cooked off in the hand)
  [Ev.OrdEnd]: ['u16', 'f32', 'f32', 'f32', 'u8'],
  // a fused grenade settled (or stuck to robot eid, 0 none) at x y z; it goes off at fuse tick
  [Ev.OrdRest]: ['u16', 'f32', 'f32', 'f32', 'u16', 'u32'],
  // crate id, weapon id, x y z, 1 spawned / 0 gone, taker pid (255 none)
  [Ev.Crate]: ['u16', 'u8', 'f32', 'f32', 'f32', 'u8', 'u8'],
  // turret id, x y z, ticks, owner pid
  [Ev.Turret]: ['u16', 'f32', 'f32', 'f32', 'u16', 'u8'],
  // turret id, dir xyz, dist, 1 shot / 255 expired
  [Ev.TurretShot]: ['u16', 'unit', 'unit', 'unit', 'f32', 'u8'],
  // eid, element (weapons Elem)
  [Ev.Proc]: ['u16', 'u8'],
  // pid, grenade type
  [Ev.Throw]: ['u8', 'u8'],
  // a posse ping or quick-chat line: pid, kind (MarkKind), x y z, robot id (0 none)
  [Ev.Mark]: ['u8', 'u8', 'f32', 'f32', 'f32', 'u16'],
  // a nemesis is posted: enemy id, name index (sim/nemesis.ts), enemy type, the cowboy it downed (255 none)
  [Ev.Nemesis]: ['u16', 'u8', 'u8', 'u8'],
  // friendly fire landed: victim pid, attacker pid (the victim themself for a cook-off), damage, 1 if it downed them
  [Ev.FriendlyHit]: ['u8', 'u8', 'u16', 'u8'],
  // stage (RunStage), reason (SaloonReason), ticks into the stage
  [Ev.Stage]: ['u8', 'u8', 'u16'],
  // pid, seat (SEATS index, NO_SEAT 255 = stood up)
  [Ev.Seat]: ['u8', 'u8'],
  [Ev.Knock]: ['u8', 'f32', 'f32', 'u8'],
  [Ev.Blink]: ['u8', 'u16', 'f32', 'f32', 'f32', 'f32', 'f32'],
  [Ev.Pound]: ['u8', 'f32', 'f32', 'f32', 'f32'],
  [Ev.Ricochet]: ['u8', 'f32', 'f32', 'f32', 'unit', 'unit', 'unit', 'f32', 'u8', 'u16', 'u8'],
  [Ev.Card]: ['u8', 'u8', 'u8'],
  [Ev.Strongbox]: ['f32', 'f32', 'f32', 'u8', 'u8'],
  [Ev.Barrel]: ['u16', 'u8', 'f32', 'f32', 'f32', 'u8', 'u8'],
  [Ev.PartPop]: ['u16', 'u8', 'f32', 'f32', 'f32'],
  [Ev.Breach]: ['u16', 'u16', 'f32', 'f32', 'f32', 'u8', 'u8'],
  // ready, needed
  [Ev.ReadyState]: ['u8', 'u8'],
  // Contract id, or 255 = none (NO_CONTRACT)
  [Ev.Contract]: ['u8'],
  // island index, building index, BStage, hp left (0..255)
  [Ev.Building]: ['u16', 'u8', 'u8', 'u8'],
  // island index, stone index, blast x y z, blast force at the stone, 1 = already fallen (a late joiner: settle at once)
  [Ev.Topple]: ['u16', 'u16', 'f32', 'f32', 'f32', 'f32', 'u8'],
  // island index, stone index, block, where it came to rest (x y z, quaternion)
  [Ev.StoneRest]: ['u16', 'u16', 'u8', 'f32', 'f32', 'f32', 'unit', 'unit', 'unit', 'unit'],
};

/** Ping and quick-chat kinds (Ev.Mark / C2S.Mark). 0 is a world ping; the rest are wheel lines. */
export const enum MarkKind { Ping = 0, PrimeIt = 1, Detonate = 2, NeedHand = 3, YeeHaw = 4 }
export const MARK_KINDS = 5;

export interface GameEvent {
  t: Ev;
  f: number[];
  s?: string;
}

export function writeEvent(w: Writer, e: GameEvent) {
  w.u8w(e.t);
  const schema = EV_SCHEMA[e.t];
  let i = 0;
  for (const k of schema) {
    if (k === 's') { w.strw(e.s ?? ''); continue; }
    const v = e.f[i++] ?? 0;
    switch (k) {
      case 'u8': w.u8w(v & 255); break;
      case 'u16': w.u16w(v & 65535); break;
      case 'u32': w.u32w(v); break;
      case 'f32': w.f32w(v); break;
      case 'cm': w.cmw(v); break;
      case 'unit': w.unitw(v); break;
    }
  }
}

export function readEvent(r: Reader): GameEvent {
  const t = r.u8() as Ev;
  const schema = EV_SCHEMA[t];
  if (!schema) throw new Error('bad event ' + t);
  const f: number[] = [];
  let s: string | undefined;
  for (const k of schema) {
    switch (k) {
      case 's': s = r.str(); break;
      case 'u8': f.push(r.u8()); break;
      case 'u16': f.push(r.u16()); break;
      case 'u32': f.push(r.u32()); break;
      case 'f32': f.push(r.f32()); break;
      case 'cm': f.push(r.cm()); break;
      case 'unit': f.push(r.unit()); break;
    }
  }
  return { t, f, s };
}
